import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpResponseBase
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ErrorCodeType,
  GoalsOperation
} from '@cpd-app/shared-module/constants';
import { environment } from '../../../environments/environment';
import { AppState } from '@cpd-app/state';
import { AuthActions, AuthSelectors } from '@cpd-app/state/auth';
import { CognitoAuthActions } from '@cpd-app/state/cognito-auth';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  finalize,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';
import { STORAGE_KEY } from '../constants/storage-key.const';
import { AuthDataService, NavigationService } from '../services';
import { resolveGraphQlStatusCode } from './graphql-error-status';

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  /**
   * Replays, so a request that arrives after initialisation finished still
   * gets the signal.
   *
   * As a plain Subject this emitted once and completed. Any request that
   * reached the queue branch afterwards subscribed to a COMPLETED subject,
   * received completion with no value, and so never reached switchMap:
   * next.handle() was never called, no HTTP went out, and the caller saw an
   * observable that completed empty. Apollo turns that into a result with
   * `data: undefined` and no errors, which is indistinguishable from a member
   * who simply has no data.
   */
  private sessionInitialized$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private queuedRequestsCount = 0; // Counter to track queued requests
  private token: string | null = null;
  private shareToken: string | null = null;
  private isAuthenticated = false;

  /*
   * ANALYTICS.22 -- is the signed-in user STAFF (counselor or administrator)?
   *
   * Staff sign in with a username and password. That path dispatches
   * `saveTokenToStorage({ token: user.token })` with NO refreshToken, because
   * there is nothing to refresh with: refresh tokens come from Cognito, and
   * Cognito is the LEARNER sign-in. A staff session therefore has no refresh
   * token by design, permanently, and always will.
   *
   * That collides with the rule below. `ErrorCodeType.SessionExpired` is '403',
   * so every permission denial arrives here looking like an expiry -- and for a
   * user who can never hold a refresh token, the branch concluded the session
   * was dead and tore it down. One "not permitted" response logged an
   * administrator out.
   *
   * Which is what Jackson hit: clicking Analytics bounced to /error/403, a
   * refresh appeared to sign him back in, and "Session expired - no refresh
   * token" kept surfacing. The admin login effect navigates to the analytics
   * dashboard on success, so staff land on the pages most likely to deny a
   * query before they touch anything.
   */
  private isStaffSession = false;
  /**
   * The refresh token the last refresh PRESENTED to the server.
   *
   * Every refresh rotates and single-uses, so this token is dead the moment
   * that call returns. Remembering it is what lets a late, token-less 403 be
   * recognised as stale news rather than spent as a second refresh -- which is
   * what signed members out mid-action.
   */
  private lastPresentedRefreshToken: string | null = null;

  private refreshInFlight$: Observable<{
    token: string;
    refreshToken: string;
  }> | null = null;

  constructor(
    private store: Store<AppState>,
    private navigationService: NavigationService,
    private authDataService: AuthDataService
  ) {
    this.store
      .select(AuthSelectors.selectIsSessionInitializing)
      .pipe(
        filter((isSessionInitializing) => !isSessionInitializing), // When initializing becomes false
        take(1)
      )
      .subscribe(() => {
        this.sessionInitialized$.next();
        this.sessionInitialized$.complete();
      });

    this.store.select(AuthSelectors.selectAuthToken).subscribe((token) => {
      this.token = token;
    });

    this.store
      .select(AuthSelectors.selectAuthSharedToken)
      .subscribe((token) => {
        this.shareToken = token;
      });

    /*
     * Signed in, as opposed to holding a guest session token. Both have a
     * token in sessionStorage and neither has a refresh token, so storage
     * alone cannot tell them apart, and treating a guest like a lapsed member
     * is what let a denied query destroy the member's work.
     */
    this.store
      .select(AuthSelectors.selectIsCounselorOrAbove)
      .subscribe((isStaff) => {
        this.isStaffSession = !!isStaff;
      });

    this.store
      .select(AuthSelectors.selectIsAuthenticated)
      .subscribe((isAuthenticated) => {
        this.isAuthenticated = !!isAuthenticated;
      });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    /*
    'X-Skip-Session-Check' is set in graphql.module
    */
    if (req.headers.has('X-Skip-Session-Check')) {
      // Bypass the session check for session initialization requests
      return next.handle(req);
    }

    return this.store.select(AuthSelectors.selectIsSessionInitializing).pipe(
      take(1),
      switchMap((isSessionInitializing) => {
        if (isSessionInitializing) {
          this.queuedRequestsCount++; // Increment the counter
          // console.log(`Request queued. Total queued requests: ${this.queuedRequestsCount}`);

          // Pause requests while session is being initialized
          return this.sessionInitialized$.pipe(
            switchMap(() => {
              this.queuedRequestsCount--; // Decrement the counter when processing
              // console.log(`Request dequeued. Remaining queued requests: ${this.queuedRequestsCount}`);

              return this.handleRequest(req, next);
            })
          );
        }

        return this.handleRequest(req, next);
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Handles the HTTP request by optionally adding an authorization token and modifying the request URL.
   * @param req - The incoming HTTP request.
   * @param next - The HTTP handler to pass the request to the next interceptor in the chain.
   * @returns An observable of the HTTP event.
   */
  private handleRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    /*
     * THE SAME OPT-OUT AuthInterceptor HONOURS.
     *
     * A caller sets 'X-Skip-Error-Redirect' to say "this endpoint renders its
     * own error state, do not replace the page". AuthInterceptor reads it;
     * this interceptor did not, and it is the OUTER of the two, so it won a
     * race the header was written to lose: the goals report calls
     * `jst/latest` on every visit with the header set, and a single failure of
     * that one endpoint navigated the whole report to /error/500.
     *
     * Read here rather than from `clonedReq` because `parseHeader` and
     * AuthInterceptor both strip it before the request leaves the client.
     */
    const skipErrorRedirect =
      req.headers.get('X-Skip-Error-Redirect') === 'true';
    const clonedReq = this.parseHeader(req);
    /*
     * The token this request actually went out with, captured before it leaves.
     * Used below to tell a denial that is worth refreshing from one this tab has
     * already moved past. See refreshRequest().
     */
    const sentToken = this.readBearerToken(clonedReq);
    return next.handle(clonedReq).pipe(
      switchMap((response) => {
        if (
          response instanceof HttpResponse &&
          response?.body?.errors?.length
        ) {
          const statusCode = resolveGraphQlStatusCode(response?.body.errors[0]);

          if (statusCode === ErrorCodeType.SessionExpired) {
            return this.refreshRequest(req, next, sentToken);
          } else {
            // Navigate to the error page based on the status code.
            if (statusCode && !skipErrorRedirect) {
              this.navigationService.goToErrorPage(statusCode);
            }
            /*
             * ANALYTICS.22 -- carry the SERVER'S message, not just a code.
             *
             * This threw `Error with status code: ${statusCode}` and nothing
             * else. When the code is null -- which is every GraphQL error that
             * carries no recognised status extension -- the whole thing read
             * "Error with status code: null", and the server's actual
             * explanation was discarded right here, at the one point that had
             * it. Chasing a failing member list produced exactly that string
             * and no way forward.
             *
             * This interceptor sits OUTSIDE Apollo, so it throws before any
             * `result.errors` handling downstream can see the response. If the
             * message is dropped here it is gone for good.
             */
            const firstError = response?.body?.errors?.[0];
            const detail =
              firstError?.message ?? 'no message returned by the server';

            // Throw an error to ensure the error is propagated.
            return throwError(
              () =>
                new Error(
                  `Error with status code: ${statusCode ?? 'none'} - ${detail}`
                )
            );
          }
        }

        return of(response);
      }),
      catchError((response) => {
        if (
          response instanceof HttpResponseBase &&
          response.status.toString() === ErrorCodeType.SessionExpired
        ) {
          return this.refreshRequest(req, next, sentToken);
        }

        // A GraphQL error can also arrive as an HttpErrorResponse (gateway
        // returning a 4xx/5xx with a GraphQL body). Classify it the same way
        // as the HttpResponse path: a 403 is refreshable, not an error page.
        const gqlStatusCode =
          response instanceof HttpErrorResponse &&
          response?.error?.errors?.length
            ? resolveGraphQlStatusCode(response.error.errors[0])
            : null;

        if (gqlStatusCode === ErrorCodeType.SessionExpired) {
          return this.refreshRequest(req, next, sentToken);
        }

        /*
         * A QUERY FAILING IS NOT A ROUTE FAILING.
         *
         * The gateway answers a document it cannot validate with an HTTP 400
         * carrying a GraphQL errors array (GRAPHQL_VALIDATION_FAILED). That
         * used to fall through to the branch below and navigate the whole app
         * to /error/400, so one page asking for a field the deployed schema
         * does not have took down every page that page sits on: the member saw
         * "Server Error" instead of a page with one section missing.
         *
         * `resolveGraphQlStatusCode` already says the rule in its own doc:
         * callers must not navigate on null, because an unclassified error is
         * not a server error. This honours it. The error still propagates, so
         * the caller can show its own absent state, and it is still logged.
         */
        /*
         * A 400 FROM THE GRAPHQL ENDPOINT IS A BAD DOCUMENT, NEVER A BAD ROUTE.
         *
         * Keyed on the URL and the status rather than on the error body's
         * shape: the body arrives as `{errors:[...]}` from some paths and as a
         * bare string from others, and checking the shape caught only one of
         * them, so the page still bounced to /error/400 via the branch below.
         *
         * A validation failure means this build asked for a field the deployed
         * schema does not have. That is worth logging loudly and worth fixing,
         * but it is not a reason to replace the page the member is on.
         */
        /*
         * Matched on the PATH, not on `environment.graphQlUri`.
         *
         * That value is null in some environments (the test config, for one),
         * and `includes(null)` quietly coerces to the string "null" and never
         * matches: the rule would switch itself off wherever the env var is
         * unset rather than failing loudly. The path is what identifies a
         * GraphQL request, whatever host it is on.
         */
        const isBadDocument =
          response instanceof HttpErrorResponse &&
          response.status === 400 &&
          /\/graphql\b/.test(response.url || req.url || '');

        /*
         * ONLY the bad-document case is lenient. Everything else falls through.
         *
         * This used to also accept any HttpErrorResponse carrying an `errors`
         * array, which has no status check in it at all: a 500 or a 503 from
         * the gateway carries one too, so a genuine outage was logged to the
         * console and hidden instead of showing the error page main showed.
         * A validation failure is the narrow case worth forgiving, and
         * `isBadDocument` is already exactly that test.
         */
        const isGraphQlFieldError = isBadDocument;

        if (skipErrorRedirect) {
          console.error(
            'Request failed; the caller renders its own error state:',
            response?.message ?? response?.status ?? 'unknown'
          );
        } else if (gqlStatusCode) {
          this.navigationService.goToErrorPage(gqlStatusCode);
        } else if (isGraphQlFieldError) {
          const detail = Array.isArray(response?.error?.errors)
            ? response.error.errors
                .map((e: { message?: string }) => e?.message)
                .join(' | ')
            : (response?.error?.message ?? response?.message ?? 'unknown');
          console.error(
            'GraphQL request failed; the page keeps rendering without it:',
            detail
          );
        } else if (response instanceof HttpErrorResponse) {
          this.navigationService.goToErrorPage(
            (response?.status || ErrorCodeType.ServerError) + ''
          );
        }
        /*
         * KEEP THE REASON. This used to throw a bare Error('An error
         * occurred') whenever the status was falsy, which discarded
         * everything the gateway had said, and the error reporter downstream
         * decides whether to interrupt the member by reading that message.
         *
         * A permission denial arrived here as "Forbidden resource" and left as
         * "An error occurred", so `ErrorEffects` could not recognize it and a
         * signed-out visitor got a red bar over a page that had rendered
         * correctly. Nine queries are denied on the skills survey alone.
         */
        const reported = Array.isArray(response?.error?.errors)
          ? response.error.errors
              .map((e: { message?: string }) => e?.message)
              .filter(Boolean)
              .join(' | ')
          : '';

        const thrown = new Error(
          response.status
            ? `HTTP error with status code: ${response.status}`
            : reported ||
              response?.error?.message ||
              // The error's OWN message, which is the case that was still
              // being flattened: not everything reaching this handler is
              // an HttpErrorResponse. An Error thrown further down the
              // chain, already carrying the reason, arrived with no
              // `status` and no `error` and so fell through to the
              // fallback and lost what it knew.
              response?.message ||
              'An error occurred'
        );
        /*
         * QA.19 U-7. A refusal (4xx) is written for the member: the
         * transcript panel reads its `code` and `message` to say what to
         * upload instead. They used to be dropped here, so the panel showed
         * "HTTP error with status code: 400". A server failure (5xx) keeps
         * its body back, so internal error text never reaches a learner.
         */
        if (
          response instanceof HttpErrorResponse &&
          response.status >= 400 &&
          response.status < 500
        ) {
          Object.assign(thrown, {
            status: response.status,
            error: response.error
          });
        }
        return throwError(() => thrown);
      })
    );
  }

  private refreshRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    sentToken: string | null = null
  ): Observable<HttpEvent<any>> {
    /*
     * A DENIAL OF A TOKEN WE HAVE ALREADY REPLACED IS STALE NEWS, NOT AN EXPIRY.
     *
     * Every refresh ROTATES: session.service.ts expires the presented refresh
     * token, expires the old session row, and issues a fresh pair. So each
     * refresh this app performs invalidates the token every in-flight request is
     * carrying, and each of those requests then comes back 403 and asked for a
     * refresh of its own. One lapse turned into a burst of rotations, and a
     * burst of rotations is what leaves other tabs, and this one, holding a
     * pair the server has already retired (CPD-652).
     *
     * If the token this request went out with is not the token we hold now, the
     * answer is simply out of date. Retry it with the current token. Nothing is
     * spent, nothing is rotated, and the member never sees it.
     */
    const currentToken = this.currentToken();

    if (sentToken && currentToken && sentToken !== currentToken) {
      return next.handle(
        req.clone({ setHeaders: { Authorization: `Bearer ${currentToken}` } })
      );
    }

    /*
     * RELEASE-1. A staff session never refreshes, so it must never SPEND a
     * refresh token either, even one an earlier learner session left in
     * localStorage. A password reset expires every other session of the
     * account; the first denied staff query then spent the leftover learner
     * token, the server answered "Invalid refresh token", and the counselor
     * who had just signed in landed on Session Expired (prod, 30 Oct). The
     * ANALYTICS.22 rule below applies whatever storage holds.
     */
    if (this.isAuthenticated && this.isStaffSession) {
      return throwError(() => new Error('Not permitted'));
    }

    const refreshToken = localStorage.getItem(STORAGE_KEY.REFRESH_TOKEN);

    /*
     * A REFRESH TOKEN WE HAVE ALREADY SPENT MUST NOT BE SPENT AGAIN.
     *
     * The guard above catches a stale denial by comparing the ACCESS token the
     * request went out with. It cannot help a request that went out with NO
     * token at all -- and those exist: the interceptor stamps the header, so
     * anything issued before the token is attached travels bare, comes back
     * 403, and arrives here with `sentToken` null.
     *
     * That request then spends whatever refresh token is in localStorage. If a
     * refresh has just rotated it, the server rejects the old one as invalid,
     * `isRefreshTokenRejected` clears both tokens and the member is thrown out
     * of the app mid-action. Reported from dev: pressing "Make a link" to
     * share a report returned FORBIDDEN "Invalid refresh token" and signed the
     * member out entirely, looking as though all their work was lost. The
     * second attempt worked, because by then the pair was consistent again.
     *
     * So: if this is the very token the last refresh already presented, and we
     * now hold an access token that refresh produced, the denial is out of
     * date in exactly the way the guard above describes. Retry with the token
     * we hold. Nothing is spent and nothing is rotated.
     */
    if (
      refreshToken &&
      refreshToken === this.lastPresentedRefreshToken &&
      currentToken
    ) {
      return next.handle(
        req.clone({ setHeaders: { Authorization: `Bearer ${currentToken}` } })
      );
    }

    if (!refreshToken) {
      /*
       * A SESSION THAT WAS NEVER REFRESHABLE IS NOT AN EXPIRED ONE.
       *
       * `ErrorCodeType.SessionExpired` is '403', so EVERY permissions denial
       * arrives here as an expiry. A guest session has no refresh token by
       * definition, so a denied query used to run this branch and tear the
       * session down: the token was cleared, the member was sent to the
       * session-expired page, and `getSessionFailed` then dispatched
       * `deleteUserProfile`, which nulls the profile the app saves from.
       *
       * That is the reported data loss. It is why survey results vanished on
       * a magic-link sign-in, on Google and LinkedIn, and on moving between
       * the home page and Build Your Target: any of those can fire a
       * permission-gated query before the token is attached, and the denial
       * took the member's work with it.
       *
       * Refreshing cannot help a session with nothing to refresh, so nothing
       * is cleared and nobody is redirected. The request still fails and the
       * caller still sees it, which is the honest outcome: not permitted.
       *
       * A member whose session HAS lapsed still has a refresh token and still
       * takes the branch below, so real expiry is untouched. A member who is
       * SIGNED IN with no refresh token is a genuinely dead session and is
       * still torn down here, which is the case the logout regression suite
       * pins.
       */
      /*
       * ANALYTICS.22. A staff session has no refresh token BY DESIGN, so the
       * absence of one says nothing about whether it is still alive. For them a
       * 403 means exactly what it says -- not permitted -- and the request is
       * failed without touching the session.
       *
       * This is deliberately narrower than "do not tear down on 403". A learner
       * who is signed in and has LOST their refresh token really does have a
       * dead session, and is still torn down below. The difference is that a
       * learner is supposed to have one and a staff member never is.
       */
      if (this.isAuthenticated && this.isStaffSession) {
        return throwError(
          () => new Error('Not permitted')
        );
      }

      if (this.isAuthenticated) {
        // Read before the dispatches: deleteTokenFromStorage synchronously
        // removes the token this checks for.
        const hadSession = this.hasSessionEvidence();

        this.store.dispatch(AuthActions.deleteTokenFromStorage());
        this.store.dispatch(AuthActions.saveTokenToState({ token: null }));

        if (hadSession) {
          this.navigationService.goToErrorPage(ErrorCodeType.SessionExpired);
        }

        return throwError(
          () => new Error('Session expired - no refresh token')
        );
      }

      return throwError(
        () => new Error('Not permitted, and no refresh token to renew with')
      );
    }

    return this.performRefresh(refreshToken).pipe(
      switchMap((refreshResponse) => {
        const retryReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${refreshResponse.token}`
          }
        });

        return next.handle(retryReq);
      }),
      catchError((refreshError) => {
        // Read before the cleanup below: deleteTokenFromStorage synchronously
        // removes the very tokens this inspects.
        const hadSession = this.hasSessionEvidence();

        /*
         * A REJECTED REFRESH TOKEN MUST NOT SURVIVE THE REJECTION.
         *
         * The server has just said this token is dead. Leaving it in
         * localStorage is what turned a lapsed session into a permanent
         * lockout: every later request found a token here, spent it, failed,
         * and arrived back at this line, while hasSessionEvidence() kept
         * reading the dead token as proof of a live session and re-issuing the
         * 403 page. Reloading, opening a new tab and restarting the browser
         * all preserve localStorage, so clearing site data by hand was the
         * only way back into CPD (CPD-652).
         *
         * Gated on a POSITIVE rejection: a dropped connection or a 5xx must
         * leave the token alone, or a flaky network would cost members their
         * session.
         */
        if (this.isRefreshTokenRejected(refreshError)) {
          this.store.dispatch(AuthActions.deleteTokenFromStorage());
          this.store.dispatch(AuthActions.saveTokenToState({ token: null }));
        }

        // Logout can wipe storage while this refresh is in flight; by the
        // time it fails the user is already signed out on purpose.
        if (hadSession) {
          this.navigationService.goToErrorPage(ErrorCodeType.SessionExpired);
        }
        return throwError(() => refreshError);
      })
    );
  }

  /**
   * "Session Expired" is for a user whose session lapsed, never for one who
   * deliberately signed out. Logout clears storage while requests are still
   * in flight, so their 403s land here with no tokens anywhere — that is the
   * intended signed-out state, not an expiry, and must not navigate to the
   * error page. Storage is the source of truth: the in-memory store token
   * stays stale until the logout page reload commits.
   */
  private hasSessionEvidence(): boolean {
    return (
      !!sessionStorage.getItem(STORAGE_KEY.TOKEN) ||
      !!localStorage.getItem(STORAGE_KEY.REFRESH_TOKEN)
    );
  }

  /**
   * Did the SERVER reject this refresh token, as opposed to the request never
   * reaching a server that could judge it?
   *
   * Only a positive rejection may delete the token. An offline laptop, a
   * dropped connection, a gateway 5xx and a timeout are all recoverable, and
   * discarding credentials on any of them would turn a momentary blip into a
   * forced sign-out.
   *
   * Three error shapes reach here, because `refreshSession` is an Apollo query
   * whose failures arrive as an ApolloError (`graphQLErrors`), as an
   * `HttpErrorResponse` carrying a GraphQL body (`error.errors`), or as a bare
   * HTTP status. `resolveGraphQlStatusCode` already knows how to read the first
   * two, including the gateway's habit of answering every denial with FORBIDDEN
   * rather than 401.
   */
  private isRefreshTokenRejected(error: any): boolean {
    const gqlErrors = [
      ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : []),
      ...(Array.isArray(error?.error?.errors) ? error.error.errors : [])
    ];

    if (
      gqlErrors.some(
        (gqlError) =>
          resolveGraphQlStatusCode(gqlError) === ErrorCodeType.SessionExpired
      )
    ) {
      return true;
    }

    return (
      error?.status === 403 || error?.networkError?.statusCode === 403
    );
  }

  /**
   * The backend rotates and single-uses refresh tokens (session.service.ts
   * invalidates the presented token and issues a new pair), so concurrent
   * callers must never each spend the same token. All callers that fail while a
   * refresh is already in flight share that one request and its result.
   */
  private performRefresh(
    refreshToken: string
  ): Observable<{ token: string; refreshToken: string }> {
    if (!this.refreshInFlight$) {
      // Recorded BEFORE the call, so a late 403 carrying no access token can
      // tell "this pair has already been rotated" from "this session lapsed".
      // See the stale-refresh guard in refreshRequest.
      this.lastPresentedRefreshToken = refreshToken;
      this.refreshInFlight$ = this.authDataService
        .refreshSession(refreshToken)
        .pipe(
          tap((refreshResponse) => {
            this.token = refreshResponse.token;
            this.store.dispatch(AuthActions.saveTokenToStorage(refreshResponse));
            this.store.dispatch(AuthActions.saveTokenToState(refreshResponse));
            this.store.dispatch(
              CognitoAuthActions.saveTokenToStorage(refreshResponse)
            );
          }),
          finalize(() => {
            this.refreshInFlight$ = null;
          }),
          // refCount deliberately false: with refCount true, if every sharer
          // unsubscribes before the refresh resolves (cancelled queries,
          // destroyed components), the HTTP call is aborted AFTER the server
          // may have rotated the token, and the new pair is never persisted —
          // the exact single-use failure this exists to prevent. Without
          // refCount the refresh always runs to completion and the tap above
          // persists the pair; finalize still resets the slot on
          // complete/error.
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }

    return this.refreshInFlight$;
  }

  /**
   * The token this interceptor would stamp on a request right now.
   *
   * Shared with parseHeader so the "has the token moved on?" comparison in
   * refreshRequest is made against the same value that was sent, share links
   * included.
   */
  private currentToken(): string | null {
    return this.shareToken ? this.shareToken : this.token;
  }

  private readBearerToken(req: HttpRequest<any>): string | null {
    const header = req.headers.get('Authorization');

    return header?.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
  }

  private parseHeader(req: HttpRequest<any>) {
    const parseToken = this.currentToken();

    return parseToken
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${parseToken}`
          }
        })
      : req;
  }
}
